import React from 'react';
import { upperFirst } from 'lodash';

import { toast } from '@dashboard/context/ToastContext';
import Button from '@dashboard/components/Button';
import Dialog from '@dashboard/components/Dialog';
import { Label } from '@dashboard/components/Form';
import { Input } from '@dashboard/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@dashboard/components/ui/table';
import { formatDateTime } from '@dashboard/lib/format-date';
import { useBulkBookingImports } from '@dashboard/hooks/queries/useAdminQueries';
import { useStartBulkBookingImport } from '@dashboard/hooks/queries/useAdminMutations';

type BulkBookingImportSummary = {
  id: string;
  state: string;
  eventId: string;
  total: number;
  booked: number;
  failedReason?: string;
  createdAt: string;
};

export default function BulkBookingImport() {
  const [file, setFile] = React.useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = React.useState(0);
  const [confirming, setConfirming] = React.useState(false);

  const bulkImports = useBulkBookingImports();

  const startImport = useStartBulkBookingImport({
    onSuccess: () => {
      // Starting the same file twice would book every row twice.
      setFile(null);
      setFileInputKey(key => key + 1);
      toast({
        title: 'Import started',
        type: 'success',
      });
    },
    onError: error => {
      toast({
        title: 'Error starting import',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const onConfirm = async () => {
    // A double click would queue the same rows twice.
    if (!confirming) return;
    setConfirming(false);
    startImport.mutate({ csv: await file.text() });
  };

  const rows: BulkBookingImportSummary[] = bulkImports.data ?? [];

  return (
    <div className="mt-4">
      <Label
        htmlFor="bulkBookingImportFile"
        description="Creates a complimentary booking for every row of a mapped.csv, one every 3 seconds. Stops at the first row that fails."
      >
        Bulk booking import
      </Label>
      <div className="flex gap-x-2">
        <Input
          key={fileInputKey}
          id="bulkBookingImportFile"
          type="file"
          accept=".csv"
          onChange={event => setFile(event.target.files?.[0] ?? null)}
          className="max-w-72"
        />
        <Button
          onClick={() => setConfirming(true)}
          appearance="primary"
          disabled={!file || startImport.isPending}
        >
          Start import
        </Button>
      </div>

      <Dialog
        open={confirming}
        onCancel={() => setConfirming(false)}
        size="md"
        title={`Create a complimentary booking for every row in ${file?.name}?`}
        description="Each attendee gets their confirmation email and tickets. Bookings already created are not undone if the import stops."
        confirmButton={{ label: 'Start import', appearance: 'primary', onClick: onConfirm }}
      />

      {rows.length > 0 && (
        <Table className="mt-3">
          <TableHeader>
            <TableRow>
              <TableHead>Started</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id}>
                <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                <TableCell>{row.eventId}</TableCell>
                <TableCell>{upperFirst(row.state)}</TableCell>
                <TableCell>
                  {row.booked} / {row.total}
                </TableCell>
                <TableCell>{row.failedReason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
